import React, { useMemo, useState } from 'react';
import NavigationStepper, {
	NavigationStepperItemType,
} from '../../../../Components/Stepper/NavigationStepper';
import HealthPlanBabyCreate from './HealthPlanBabyCreate';
import { useHealthPlanContext } from '../../../../Services/HealthPlanContext';
import RegularExaminationSection from './PlanSections/RegularExaminationSection';
import PregnancyCard from './PlanSections/PregnancyCard';
import WorkplaceCalendar from '../../../../Components/Calendar/WorkplaceCalendar';
import EndPlanSection from './PlanSections/EndPlanSection';
import PatientsQuestionnaires from '../../../Questionnaire/Components/PatientsQuestionnaires/PatientsQuestionnaires';
import { QuestionnaireModule } from '../../../Questionnaire/Enums/QuestionnaireModule.enum';

interface Props {
	patientId: string;
	workplaceId: string;
	onHealthPlanCreate: () => void;
}

const HealthPlanBabyCzech: React.FC<Props> = ({
	patientId,
	workplaceId,
	onHealthPlanCreate,
}) => {
	const { healthPlanUser, healthPlan, refetchHealthPlanUser } =
		useHealthPlanContext();

	const handleEndSuccess = () => {
		refetchHealthPlanUser();
	};

	const steps: NavigationStepperItemType[] = useMemo(() => {
		const tempSteps: NavigationStepperItemType[] = [];

		if (healthPlanUser) {
			tempSteps.push({
				name: 'component.meddiBabyCzech.pregnancyCard',
				key: 'pregnancyCard',
				component: () => (
					<PregnancyCard
						patientId={patientId}
						healthPlanCode={healthPlan.code}
						healthPlanUser={healthPlanUser}
					/>
				),
			});

			if (!healthPlanUser.hasEnded) {
				tempSteps.push({
					name: 'component.meddiBabyCzech.regularInspection',
					key: 'regularInspection',
					component: ({ setActiveStepPath }) => (
						<RegularExaminationSection
							patientId={patientId}
							workplaceId={workplaceId}
							onSubmit={() => {
								setActiveStepPath('mothersHealth');
							}}
						/>
					),
				});
				tempSteps.push({
					name: 'component.meddiBabyCzech.monitoring.label',
					key: 'questionnaires',
					component: () => (
						<PatientsQuestionnaires
							patientId={patientId}
							module={QuestionnaireModule.MEDDIBABY}
							roundValuesTo={1}
						/>
					),
				});

				tempSteps.push({
					name: 'component.meddiBabyCzech.calendar',
					key: 'calendar',
					component: () => (
						<WorkplaceCalendar
							workplaceId={workplaceId}
							patientId={patientId}
						/>
					),
				});

				tempSteps.push({
					name: 'component.meddiBabyCzech.endPlan',
					key: 'cancelHealthPlan',
					component: () => (
						<EndPlanSection
							healthPlanUser={healthPlanUser}
							patientId={patientId}
							onSuccess={handleEndSuccess}
						/>
					),
				});
			}

			if (healthPlanUser.hasEnded) {
				tempSteps.push({
					name: 'component.meddiBabyCzech.monitoring.label',
					key: 'questionnaires',
					component: () => (
						<PatientsQuestionnaires
							patientId={patientId}
							module={QuestionnaireModule.MEDDIBABY}
						/>
					),
				});
			}
		} else {
			tempSteps.push({
				name: 'component.meddiBabyCzech.createHealthPlan',
				key: 'healthPlanBabyCreate',
				component: () => (
					<HealthPlanBabyCreate
						patientId={patientId}
						workplaceId={workplaceId}
						onHealthPlanCreate={onHealthPlanCreate}
					/>
				),
			});
		}

		return tempSteps;
	}, [healthPlanUser]);

	return (
		<NavigationStepper
			steps={steps}
			initialStepPath='pregnancyCard'
			canSkip={true}
			showStepNumbers={false}
		/>
	);
};

export default HealthPlanBabyCzech;
